import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import SvgMinimize from '@material-ui/icons/Minimize';
import SvgMaximize from '@material-ui/icons/Maximize';

import {http, api} from '../common/common';
import ViewStack from './ViewStack';

const styles = (theme) => {
    return {
        paper :{
            position: 'relative',
            display: 'inline-block',
            maxWidth: 275,
            minWidth: 275,
            maxHeight: 'calc(100% - 80px)',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 8,
            paddingBottom: 8,
            color: theme.palette.primary.displayText,
            backgroundColor: theme.palette.primary.main,
            '&$noContent': {
                minWidth: 0,
                height: 32,
                paddingTop: 0,
                paddingBottom: 0,
            },
        },
        scrollView: {
            maxHeight: 'calc(100% - 48px)',
            overflowY: 'auto',
        },
        title: {
            color: theme.palette.primary.displayText,
            paddingBottom: 0,
            height: 32,
            fontSize: 14,
            lineHeight: '32px',
            '&$noContent': {
                paddingBottom: 0,
            },
        },
        contentBlock: {
            height: 24,
            marginTop: 3,
            marginBottom: 3,
            whiteSpace: 'nowrap',
            cursor: 'default',
        },
        labelText: {
            float: 'left',
            height: 24,
            lineHeight: '24px',
            fontSize: 14,
            fontWeight: 300,
            paddingRight: 24,
            color: theme.palette.primary.displayText,
            verticalAlign: 'middle',
            fontVariant: 'small-caps',
        },
        valueText: {
            float: 'right',
            height: 24,
            lineHeight: '24px',
            fontSize: 12,
            fontWeight: 500,
            color: theme.palette.primary.displayText,
            verticalAlign: 'middle',
        },
        tooltip: {
            maxWidth: 300,
        },
        divider: {
            marginTop: 12,
            marginBottom: 12,
            marginLeft: 4,
            marginRight: 4,
            backgroundColor: theme.palette.primary.displayText,
        },
        colorBlock: {
            float: 'left',
            width: 16,
            height: 16,
            marginTop: 3,
            marginBottom: 3,
            borderColor: theme.palette.primary.displayText,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: theme.palette.primary.main,
        },
        minMaxButton: {
            position: 'absolute',
            top: 16,
            right: 16,
            width: 16,
            height: 16,
            borderColor: theme.palette.primary.displayText,
            borderRadius: 2,
            borderWidth: 1,
            borderStyle: 'solid',
            cursor: 'pointer',
        },
        minMaxIcon: {
            fontSize: 16,
        },
        noContent: {}
    };
}

class InfoPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            timestamp: 0,
            maximize: true,
        };

        this._title = '';
        this._content = [];
        this._colorBlock = [];
        this._cancelToken = http.CancelToken.source();
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        const nextView = nextProps.viewStack.peek();

        if (!nextView.layer) {
            return false;
        }

        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {

            this._content = [];
            this._colorBlock = [];
        }

        return (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex ||
            this.state.timestamp !== nextState.timestamp ||
            this.state.maximize !== nextState.maximize);
    }

    componentDidUpdate = (prevProps, prevState) => {
        const nextView = this.props.viewStack.peek();
        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {

            this._uid = nextUid;
            this._layerType = nextLayerType;
            this._layerIndex = nextLayerIndex;

            this._queryData();
        }
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        if (this.props.viewStack.isDefaultBase()) {
            return null;
        }

        if (!this._title) {
            return null;
        }

        const haveContent = this._content.length;
        const haveLegend = this._colorBlock.length;
        const haveData = (haveContent || haveLegend);

        const {classes} = this.props;

        return (
            <div className={this.props.className}>
                <Paper
                    className={classNames(classes.paper, haveData ? null : classes.noContent)}
                    square={false}
                    elevation={2}
                >
                    <Typography
                        className={classes.title}
                        align='left'
                        variant='subtitle1'
                    >
                        {this._title}
                    </Typography>
                    {(haveData) ? this._renderMaximizeContent() : null}
                </Paper>
            </div>
        );
    }

    _renderMaximizeContent = () => {
        const {classes} = this.props;

        if (!this.state.maximize) {
            return (
                <div
                    className={classes.minMaxButton}
                    onClick={this._toggleMinMax}
                >
                    <SvgMaximize className={classes.minMaxIcon}/>
                </div>
            );
        }

        return (
            <div className={classes.scrollView}>
                {this._content.map(this._renderInfoLine)}
                {this._renderDivider(this._content.length, this._colorBlock.length)}
                {this._colorBlock.map(this._renderColorBlockLine)}
                <div
                    className={classes.minMaxButton}
                    onClick={this._toggleMinMax}
                >
                    <SvgMinimize className={classes.minMaxIcon}/>
                </div>
            </div>
        );
    }

    _renderDivider = (hasPrevBlock, hasNextBlock) => {
        const {classes} = this.props;

        if (hasPrevBlock && hasNextBlock) {
            return (
                <Divider
                    className={classes.divider}
                    light={true}
                    variant='middle'
                />
            );
        }

        return null;
    }

    _renderInfoLine = (content, index) => {
        const {classes} = this.props;

        const tooltipClasses = {
            tooltip: classes.tooltip,
        };

        return (
            <Tooltip
                classes={tooltipClasses}
                key={index}
                title={content.desc ? content.desc : ''}
                placement='top-start'
                enterDelay={500}
            >
                <div className={classes.contentBlock}>
                    <Typography
                        className={classes.labelText}
                        align='left'
                    >
                        {content.label}
                    </Typography>
                    <Typography
                        className={classes.valueText}
                        align='right'
                    >
                        {content.data}
                    </Typography>
                </div>
            </Tooltip>
        );
    }

    _renderColorBlockLine = (data, index, arr) => {
        const {classes} = this.props;

        const style = {
            backgroundColor: this._resetOpacity(data.color, 0.87),
        }

        return (
            <div
                className={classes.contentBlock}
                key={index}
            >
                <div
                    className={classes.colorBlock}
                    key={index}
                    style={style}
                />
                <Typography
                    className={classes.valueText}
                    align='right'
                >
                    {data.label}
                </Typography>
            </div>
        );
    }

    _toggleMinMax = (event) => {
        event.preventDefault();

        this.setState({
            maximize: !this.state.maximize
        });
    }

    _resetOpacity = (hex, opacity) => {
        hex = hex.replace('#','');
        const r = parseInt(hex.substring(0,2), 16);
        const g = parseInt(hex.substring(2,4), 16);
        const b = parseInt(hex.substring(4,6), 16);

        return 'rgba(' + r + ',' + g + ',' + b + ',' + opacity + ')';
    }

    _signalError = () => {
        this.setState({
            timestamp: Date.now() - 10,
        });
    }

    _needDisplayLegend = (colorBlock) => {
        const length = colorBlock.length;

        if (length === 0) {
            return false;
        }

        if (length <= 2) {
            return true;
        }

        var labelCount = 0;

        for (var i = 1; i < length - 1; ++i) {
            if (colorBlock[i].label) {
                ++labelCount;
            }
        }

        return (labelCount > 0)
    }

    _queryData = () => {
        const view = this.props.viewStack.peek();
        const layerType = view.selectedLayerType;

        if (!view.layer[layerType] || this.props.viewStack.isDefaultBase()) {
            this._signalError();
            return;
        }

        this._title = view.layer[layerType].name;

        this._requestAll(view);
    }

    _requestAll = async (view) => {
        await Promise.all([this._requestInfoData(view), this._requestLegend(view)]);

        this.setState({
            timestamp: Date.now(),
        });
    }

    _requestInfoData = async (view) => {
        try {
            const response = await http.get(api.map() + '/info', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': view.organization,
                    'region': view.key,
                    'layer_type': view.selectedLayerType,
                    'layer_key': view.selectedLayerData.originalIndexKey,
                },
                cancelToken: this._cancelToken.token,
            });

            this._content = [];

            if (response.data.content.entry) {
                for (const entry of response.data.content.entry) {
                    this._content.push(entry);
                }
            } else if (response.data.content.label && response.data.content.data) {
                this._content.push({
                    label: response.data.content.label,
                    data: response.data.content.data,
                });
            }

        } catch (error) {
            this._content = [];

            if (http.isCancel(error)) {
                return;
            }
        }
    }

    _requestLegend = async (view) => {
        try {
            const response = await http.get(api.map() + '/legend', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': view.organization,
                    'region': view.key,
                    'layer_type': view.selectedLayerType,
                    'layer_key': view.selectedLayerData.originalIndexKey,
                },
                cancelToken: this._cancelToken.token,
            });

            if (response.data.bin) {
                this._colorBlock = response.data.bin;
                this._colorBlock.reverse();

                if (!this._needDisplayLegend(this._colorBlock)) {
                    this._colorBlock = [];
                }
            }

        } catch (error) {
            this._colorBlock = [];

            if (http.isCancel(error)) {
                return;
            }
        }
    }
}

InfoPanel.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    viewStack: PropTypes.instanceOf(ViewStack).isRequired,
}

export default withStyles(styles)(InfoPanel);
